import { useState, type FormEvent } from "react";
import { useKit, type QuotationDetail } from "../../api/quotations";

/** The standard service kit the quotation was started with, what it could not
 * settle, and the dialog that switches it while the quotation is a draft. The
 * dialog opens on the quotation's current kit, not on the last pick tried, and
 * after a switch it names the codes the kit left to lines the office keyed,
 * since those stay at the office's own quantity and price. */
export function KitPanel({ detail }: { detail: QuotationDetail }) {
  const kit = useKit(detail.id);
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState("");
  const [pilot, setPilot] = useState("");
  const [skipped, setSkipped] = useState<string[]>([]);
  // The note and the kit summary outlive the draft: once Macsteel has confirmed
  // a quotation is exactly when the office needs to know why a pilot is
  // missing from it. Only the switch is draft-only.
  const editable = detail.actions.can_edit && detail.status === "draft";
  if (!editable && !detail.kit.note && !detail.kit.size_label) {
    return null;
  }
  const hasKits = detail.kits.sizes.length > 0;

  function toggle() {
    if (!open) {
      setSize(detail.kit.size_label ?? "");
      setPilot(detail.kit.pilot ?? "");
      setSkipped([]);
    }
    setOpen((o) => !o);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    kit.mutate(
      { size_label: size, pilot: pilot || null },
      {
        onSuccess: (answer) => {
          setSkipped(answer.kit_result?.skipped ?? []);
          setOpen(false);
        },
      },
    );
  }

  return (
    <div className="cm-inline-box">
      {detail.kit.note ? <p className="notice">{detail.kit.note}</p> : null}
      {detail.kit.size_label ? (
        <p className="cm-doc-note">
          Standard service kit: {detail.kit.size_label}
          {detail.kit.pilot ? `, pilot ${detail.kit.pilot}` : ", no pilot kit"}.
        </p>
      ) : null}
      {skipped.length > 0 ? (
        <p className="notice">
          The kit did not add {skipped.join(", ")}: a bare code is already on a line you keyed, which stays at your
          own quantity and price.
        </p>
      ) : null}
      {editable ? (
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!hasKits}
          aria-expanded={open}
          onClick={toggle}
        >
          Standard service
        </button>
      ) : null}
      {editable && !hasKits ? <p className="cm-doc-note">The price list in force has no service kits.</p> : null}
      {editable && open && hasKits ? (
        <form className="cm-form" aria-label="Standard service kit" onSubmit={onSubmit}>
          <label>
            Valve size
            <select value={size} onChange={(e) => setSize(e.target.value)}>
              <option value="">Choose…</option>
              {detail.kits.sizes.map((s) => (
                <option key={s.size_label} value={s.size_label}>
                  {s.size_label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Pilot
            <select value={pilot} onChange={(e) => setPilot(e.target.value)}>
              <option value="">None</option>
              {detail.kits.pilots.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={kit.isPending || size === ""}>
            Apply kit
          </button>
          <p className="cm-doc-note">Replaces the lines the kit added; lines you keyed stay.</p>
          {kit.isError ? <p className="notice bad">{kit.error.message}</p> : null}
        </form>
      ) : null}
    </div>
  );
}
